import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { logActivity } from '../services/api';

export const CodeHistoryItem = ({ code, onHistoryItemClick, showArrow = true }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    
    const handleClick = async (e) => {
        e.preventDefault();
        
        // First - trigger URL navigation and log activity
        try {
            window.open(code.url, '_blank');
            await logActivity('visit_code', code.code, code.code_id, code.owner_id);
        } catch (error) {
            console.error('Error logging activity:', error);
        }

        // Then notify parent about the click to handle detailed code cleanup
        if (onHistoryItemClick) {
            onHistoryItemClick(code);
        }
    };
    
    return (
        <Box 
            component="a"
            href={code.url}
            target="_blank"
            onClick={handleClick}
            sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 1.25, sm: 2 },
                width: '100%',
                p: { xs: 1.25, sm: 1.75 },
                backgroundColor: isDark ? '#141E30' : '#FFFFFF',
                borderRadius: '14px',
                border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(15, 23, 42, 0.08)'}`,
                boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 6px rgba(15, 23, 42, 0.03)',
                cursor: 'pointer',
                textDecoration: 'none',
                color: theme.palette.text.primary,
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                    backgroundColor: isDark ? '#18243A' : '#F8FAFC',
                    borderColor: theme.palette.primary.main,
                    transform: 'translateY(-2px)',
                    boxShadow: isDark ? '0 8px 20px rgba(0, 0, 0, 0.5)' : '0 8px 20px rgba(15, 23, 42, 0.08)',
                    '& .history-arrow': {
                        transform: 'translateX(3px)',
                        color: theme.palette.primary.main,
                    }
                }
            }}
        >
            {/* Thumbnail */}
            <Box
                sx={{
                    width: { xs: 46, sm: 54 },
                    height: { xs: 46, sm: 54 },
                    flexShrink: 0,
                    borderRadius: '10px',
                    overflow: 'hidden',
                    backgroundColor: isDark ? '#1E293B' : '#F1F5F9',
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {code.imageUrl ? (
                    <img
                        src={code.imageUrl}
                        alt={code.code}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                ) : (
                    <Box sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.primary.main,
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: { xs: '0.9rem', sm: '1.05rem' }
                    }}>
                        {code.code ? code.code.substring(0, 2) : <ImageIcon sx={{ fontSize: 20 }} />}
                    </Box>
                )}
            </Box>

            {/* Info details */}
            <Box sx={{ 
                flex: 1, 
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, minWidth: 0, width: '100%' }}>
                    <Typography 
                        variant="body1"
                        sx={{ 
                            color: theme.palette.primary.main,
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            fontWeight: 800,
                            letterSpacing: '0.02em',
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {code.code}
                    </Typography>
                    {code.codeName && (
                        <Typography 
                            variant="body2"
                            sx={{ 
                                color: theme.palette.text.primary,
                                fontWeight: 600,
                                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                                flex: 1,
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            • {code.codeName}
                        </Typography>
                    )}
                </Box>
                <Typography 
                    variant="body2"
                    noWrap
                    sx={{ 
                        color: theme.palette.text.secondary,
                        fontSize: { xs: '0.75rem', sm: '0.85rem' },
                        lineHeight: 1.2
                    }}
                >
                    {code.codeDescription || code.url}
                </Typography>
            </Box>

            {/* Arrow action badge */}
            {showArrow && (
                <Box 
                    className="history-arrow"
                    sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.text.secondary,
                        width: 32,
                        height: 32,
                        borderRadius: '8px',
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(15, 23, 42, 0.04)',
                        transition: 'all 0.2s ease',
                        flexShrink: 0
                    }}
                >
                    <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                </Box>
            )}
        </Box>
    );
};